import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import dayjs from "dayjs";
import React from "react";
import { useEffect } from "react";
import { useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { useReactToPrint } from "react-to-print";

const headerStyle = {
  p: 1,
  textAlign: "center",
  bgcolor: "#4527a0",
  color: "#FFF",
};
const cellStyle = {
  p: 0,
  textAlign: "center",
  border: "1px solid black",
};

// Transaction type mapping function
const getTransactionTypeLabel = (type) => {
  const typeMap = {
    SI: "SI",
    SR: "SR",
    Cash: "Cash",
    RTGS: "RTGS",
    Cheque: "Cheque",
    DD: "DD",
  };
  return typeMap[type] || type;
};

function EmployeeReportPrint() {
  const { companyInfo } = useSelector((state) => state?.common);
  const logo = JSON.parse(companyInfo?.logo);
  const location = useLocation();
  const { tabledata, fromDate, toDate, godown, salesType, totals } =
    location.state || {};
  const navigate = useNavigate();
  const componentRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onAfterPrint: () => {
      navigate(-2);
    },
  });
  
  useEffect(() => {
    handlePrint();
  }, [handlePrint]);

  
  const getColumns = () => {
    const baseColumns = [
      { headerName: "SINO" },
      { headerName: "Date" },
      { headerName: "Bill No" },
      { headerName: "Party Name" },
      { headerName: "Description" },
      { headerName: "Transaction Type" },
    ];

    
    if (salesType === "SALES") {
      baseColumns.push({ headerName: "Sale Type" });
    }

    baseColumns.push({ headerName: "Amount" });
    
    return baseColumns;
  };

  const columns = getColumns();

  
  const getProcessedData = () => {
    if (!tabledata || tabledata.length === 0) return { displayData: [] };

    if (salesType === "SALES") {
      
      const salesInvoices = tabledata.filter((item) => item.transaction_type === "SI");
      const salesReturns = tabledata.filter((item) => item.transaction_type === "SR");

     
      const sortedInvoices = [...salesInvoices].sort((a, b) => {
        const dateA = dayjs(a.date, "DD-MM-YY");
        const dateB = dayjs(b.date, "DD-MM-YY");
        return dateA.valueOf() - dateB.valueOf();
      });

      const sortedReturns = [...salesReturns].sort((a, b) => {
        const dateA = dayjs(a.date, "DD-MM-YY");
        const dateB = dayjs(b.date, "DD-MM-YY");
        return dateA.valueOf() - dateB.valueOf();
      });

      
      const invoicesWithSino = sortedInvoices.map((item, index) => ({
        ...item,
        SINO: index + 1,
      }));

      const returnsWithSino = sortedReturns.map((item, index) => ({
        ...item,
        SINO: invoicesWithSino.length + index + 1,
      }));

      return {
        displayData: [...invoicesWithSino, ...returnsWithSino],
        invoices: invoicesWithSino,
        returns: returnsWithSino,
      };
    } else {
      
      const displayData = tabledata.map((item, index) => ({
        ...item,
        SINO: index + 1,
      }));

      return {
        displayData: displayData,
      };
    }
  };

  const processedData = getProcessedData();

 
  const renderTableContent = () => {
    if (!tabledata || tabledata.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={columns.length} sx={{ textAlign: "center", p: 3 }}>
            <Typography variant="h6" color="textSecondary">
              No data found
            </Typography>
          </TableCell>
        </TableRow>
      );
    }

    if (salesType === "SALES") {
      return (
        <>
          
          {processedData.invoices?.map((data, idx) => (
            <TableRow key={`inv-${idx}`}>
              <TableCell sx={cellStyle}>{data.SINO}</TableCell>
              <TableCell sx={cellStyle}>{data.date}</TableCell>
              <TableCell sx={cellStyle}>{data.billNo}</TableCell>
              <TableCell sx={cellStyle}>{data.partyName}</TableCell>
              <TableCell sx={cellStyle}>{data.description || "-"}</TableCell>
              <TableCell sx={cellStyle}>
                {getTransactionTypeLabel(data.transaction_type)}
              </TableCell>
              {salesType === "SALES" && (
                <TableCell sx={cellStyle}>{data.SaleType || "-"}</TableCell>
              )}
              <TableCell sx={cellStyle}>
                {parseFloat(data.amount || 0).toFixed(2)}
              </TableCell>
            </TableRow>
          ))}

          
          {processedData.invoices?.length > 0 && (
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell
                colSpan={salesType === "SALES" ? 7 : 6}
                align="center"
                sx={{ ...cellStyle, fontWeight: "bold" }}
              >
                Sales Invoice Total
              </TableCell>
              <TableCell sx={{ ...cellStyle, fontWeight: "bold" }}>
                {totals?.invoiceTotal?.toFixed(2) || "0.00"}
              </TableCell>
            </TableRow>
          )}

          
          {processedData.returns?.map((data, idx) => (
            <TableRow key={`ret-${idx}`}>
              <TableCell sx={cellStyle}>{data.SINO}</TableCell>
              <TableCell sx={cellStyle}>{data.date}</TableCell>
              <TableCell sx={cellStyle}>{data.billNo}</TableCell>
              <TableCell sx={cellStyle}>{data.partyName}</TableCell>
              <TableCell sx={cellStyle}>{data.description || "-"}</TableCell>
              <TableCell sx={cellStyle}>
                {getTransactionTypeLabel(data.transaction_type)}
              </TableCell>
              {salesType === "SALES" && (
                <TableCell sx={cellStyle}>{data.SaleType || "-"}</TableCell>
              )}
              <TableCell sx={cellStyle}>
                {parseFloat(data.amount || 0).toFixed(2)}
              </TableCell>
            </TableRow>
          ))}

          
          {processedData.returns?.length > 0 && (
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell
                colSpan={salesType === "SALES" ? 7 : 6}
                align="center"
                sx={{ ...cellStyle, fontWeight: "bold" }}
              >
                Sales Return Total
              </TableCell>
              <TableCell sx={{ ...cellStyle, fontWeight: "bold" }}>
                {totals?.returnTotal?.toFixed(2) || "0.00"}
              </TableCell>
            </TableRow>
          )}

          
          {(processedData.invoices?.length > 0 || processedData.returns?.length > 0) && (
            <TableRow sx={{ backgroundColor: "#e0e0e0" }}>
              <TableCell
                colSpan={salesType === "SALES" ? 7 : 6}
                align="center"
                sx={{ ...cellStyle, fontWeight: "bold" }}
              >
                Grand Total
              </TableCell>
              <TableCell sx={{ ...cellStyle, fontWeight: "bold" }}>
                {totals?.grandTotal?.toFixed(2) || "0.00"}
              </TableCell>
            </TableRow>
          )}
        </>
      );
    } else {
      
      return (
        <>
          {processedData.displayData?.map((data, idx) => (
            <TableRow key={idx}>
              <TableCell sx={cellStyle}>{data.SINO}</TableCell>
              <TableCell sx={cellStyle}>{data.date}</TableCell>
              <TableCell sx={cellStyle}>{data.billNo}</TableCell>
              <TableCell sx={cellStyle}>{data.partyName}</TableCell>
              <TableCell sx={cellStyle}>{data.description || "-"}</TableCell>
              <TableCell sx={cellStyle}>
                {getTransactionTypeLabel(data.transaction_type)}
              </TableCell>
              <TableCell sx={cellStyle}>
                {parseFloat(data.amount || 0).toFixed(2)}
              </TableCell>
            </TableRow>
          ))}

          <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
            <TableCell
              colSpan={salesType === "SALES" ? 7 : 6}
              align="center"
              sx={{ ...cellStyle, fontWeight: "bold" }}
            >
              {salesType === "COLLECTION" ? "Collection Total" : "Expense Total"}
            </TableCell>
            <TableCell sx={{ ...cellStyle, fontWeight: "bold" }}>
              {totals?.total?.toFixed(2) || "0.00"}
            </TableCell>
          </TableRow>
        </>
      );
    }
  };

  const getReportTitle = () => {
    switch(salesType) {
      case "SALES": return "Employee Sales Report";
      case "COLLECTION": return "Employee Collection Report";
      case "EXPENSE": return "Employee Expense Report";
      default: return "Employee Report";
    }
  };

  return (
    <div ref={componentRef}>
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Box px={2}>
          <Box
            component={"img"}
            sx={{ height: "auto", width: 120 }}
            src={logo?.img}
          />
        </Box>
        <Box>
          <Typography variant="h2" fontWeight={"bold"}>
            {getReportTitle()}
          </Typography>
          <Box>
            Date{" "}
            {`${fromDate} - ${toDate}`}
          </Box>
          <Box>
            Branch: {godown}
          </Box>
        </Box>
        <Box px={2}>
          <Stack>
            <Typography textAlign={"center"} variant="h3">
              {companyInfo?.Name}
            </Typography>
            <Typography
              textAlign={"center"}
              variant="h5"
            >{`${companyInfo?.Add1} ${companyInfo?.Add2}`}</Typography>
            <Typography textAlign={"center"} variant="h5">
              {companyInfo?.Ph}
            </Typography>
            <Typography textAlign={"center"} variant="h5">
              {companyInfo?.city_name} {companyInfo?.Place}
            </Typography>
            <Typography textAlign={"center"} variant="h5">
              {companyInfo?.EMail}
            </Typography>
          </Stack>
        </Box>
      </Stack>
      
      <TableContainer sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns?.map((data, idx) => {
                return (
                  <TableCell sx={headerStyle} key={idx}>
                    {data.headerName}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {renderTableContent()}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}

export default EmployeeReportPrint;